/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Target } from '@fluentui/react/lib/Callout';
import { FocusZone, FocusZoneDirection } from '@fluentui/react/lib/FocusZone';
import { getFirstFocusable, getId } from '@fluentui/react/lib/Utilities';
import flatten from 'lodash.flatten';
import orderBy from 'lodash.orderby';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { entityColors } from 'src/core/themes/themeBuilder';
import { oxfordJoin } from 'src/core/utils/array';
import { mergeFunctionObjects } from 'src/core/utils/mergeFunctionObjects';
import { TokenSelectionStore } from 'src/luis/modules/labeling/utils/TokenSelectionStore';
import { isMachineLearned } from 'src/luis/projects/entities/utils/entityHelpers';
import { LayoutManager } from 'src/modules/labeling/components/LayoutManager';
import { TokenUIProps } from 'src/modules/labeling/components/Line';
import { Shape } from 'src/modules/labeling/components/Shape';
import { Token } from 'src/modules/labeling/components/Token';
import { useGlobalLabelingEventListeners, useTokenLabelingEventListeners } from 'src/modules/labeling/hooks/useLabelingEventListeners';
import { useResizeBehavior } from 'src/modules/labeling/hooks/useResizeBehavior';
import { useSelectionEffect } from 'src/modules/labeling/hooks/useSelectionEffect';
import { Label, MultilineLabel } from 'src/modules/labeling/types/Label';
import { Selection } from 'src/modules/labeling/types/Selection';
import { ShapeUIProps } from 'src/modules/labeling/types/ShapeUIProps';
import { ViewOptions } from 'src/modules/labeling/types/ViewOptions';
import { groupLabelsByBoundaries } from 'src/modules/labeling/utils/groupLabelsByBoundaries';
import { depthColorsMap } from 'src/modules/labeling/utils/labelingConstants';
import { INVALID_INDEX } from 'src/modules/labeling/utils/labelingReducer';
import { getFirstTokenElement, getTokenElementByIndex } from 'src/modules/labeling/utils/layout';

const messages = defineMessages({
    conjunction: {
        id: 'labeling.labeler.conjunction',
        defaultMessage: 'and'
    },
    labelAriaLabel: {
        id: 'labeling.labeler.labelAriaLabel',
        defaultMessage: '"{text}" is wrapped by {labelsDisplay}'
    },
    labelerAriaLabel: {
        id: 'labeling.labeler.labelerAriaLabel',
        defaultMessage: 'Utterance text: {originalText}. Labels: {labelsAriaLabel}. Predictions: {predictionsAriaLabel}'
    },
    emptyLabels: {
        id: 'labeling.labeler.emptyLabels',
        defaultMessage: 'The utterance has no labels'
    },
    emptyPredictions: {
        id: 'labeling.labeler.emptyPredictions',
        defaultMessage: 'The utterance has no predictions'
    }
});

export type LabelerConfig = {
    /**
     * Indicates whether the labeling is disabled or not.
     */
    disabled?: boolean;

    /**
     * Labeling view options.
     */
    viewOptions?: ViewOptions;

    /**
     * Disables the tokens hover brackets.
     */
    disableHoverBrackets?: boolean;

    /**
     * Disables resizing labels and predictions.
     */
    disableResizingLabels?: boolean;

    /**
     * Whether the utterance is RTL or not.
     */
    isRTL?: boolean;

    /**
     * Virtualizes labelers lines.
     */
    useVirtualization?: boolean;

    /**
     * Make visual tweaks to the labeler to make
     * it compatible with char tokenization.
     * Note: For better performance use this in conjunction
     * with useVirtualization prop set to true.
     */
    useCharTokenization?: boolean;

    /**
     * Function used for resolving shapes' colors.
     */
    resolveShapeColor?: (shapeProps: ShapeUIProps) => string;
};

export type LabelerProps = {
    /**
     * Array of utterance tokens.
     */
    linesTokens: string[][];

    /**
     * Utterance's text.
     */
    text: string;

    /**
     * Array of labels.
     */
    labels: Label[];

    /**
     * Array of booleans indicating the state of every token whether it has a conflict or not.
     */
    conflicts: boolean[];

    /**
     * CSS style object to get applied to the root element.
     */
    style?: React.CSSProperties;

    /**
     * CSS class to get applied to the root element.
     */
    className?: string;

    /**
     * Callback that gets called when the selection changes.
     */
    onSelect: (selection?: Selection, target?: Target) => void;

    /**
     * Callback that gets invoked when a shape is clicked.
     */
    onShapeClick: (label: Label, target: Target) => void;

    /**
     * Callback that gets invoked when a token is hovered.
     */
    onTokenHover: (tokenIndex: number) => void;

    /**
     * Callback that gets invoked when the user exits hovering the utterance.
     */
    onTokenHoverExit: () => void;

    /**
     * The content of the tooltip that rendered while label hovering.
     */
    labelTooltipContent?: (label: Label) => JSX.Element;

    /**
     * Callback function that gets invoked when a label or a prediction gets resized.
     */
    onLabelResize: (label: Label) => void;

    /**
     * Callback function that get invoked when there is
     * an active selection and the user clicks delete.
     */
    onRemoveRange?: (start: number, end: number) => void;

    /**
     * Labeler config object.
     */
    config?: LabelerConfig;
};

export type LabelerRef = { cancelSelection: () => void; htmlElement: HTMLSpanElement };

const defaultConfig: LabelerConfig = {
    isRTL: false,
    disabled: false,
    disableHoverBrackets: false,
    disableResizingLabels: true,
    viewOptions: { hideNames: false, isBold: false, displayGutter: false, hasLabelBorders: true },
    useVirtualization: false,
    useCharTokenization: false,
    resolveShapeColor: ({ entityType, depth }) => (isMachineLearned(entityType) ? depthColorsMap[depth] : entityColors.defaultColor)
};

/**
 * This is the cornerstone of the labeling module. This component has the following responsibilities:
 * 1) Uses the `LayoutManager` to layout tokens and shapes.
 * 2) Attaches event handlers for the labeling actions.
 * 3) Manages token selection.
 * Note: Don't make this component observer to avoid subscribing to mobx state and
 * render unnecessarily. Individual tokens and shapes should do the subscriptions instead.
 */
export const Labeler = React.memo(
    React.forwardRef((props: LabelerProps, labelerRef: React.MutableRefObject<LabelerRef>) => {
        const {
            className,
            labels,
            onSelect,
            style,
            linesTokens,
            text,
            config: {
                isRTL = defaultConfig.isRTL,
                disabled = defaultConfig.disabled,
                viewOptions: viewOptionsProp = defaultConfig.viewOptions,
                useVirtualization = defaultConfig.useVirtualization,
                useCharTokenization = defaultConfig.useCharTokenization,
                resolveShapeColor = defaultConfig.resolveShapeColor,
                disableHoverBrackets = defaultConfig.disableHoverBrackets,
                disableResizingLabels = defaultConfig.disableResizingLabels
            } = defaultConfig,
            conflicts,
            onShapeClick,
            onTokenHover,
            onTokenHoverExit,
            onLabelResize,
            labelTooltipContent,
            onRemoveRange,
            ...rest
        } = props;

        const viewOptions = React.useMemo(() => ({ ...defaultConfig.viewOptions, ...viewOptionsProp }), [viewOptionsProp]);

        const containerId = React.useRef<string>(getId()).current;
        const refFocusZone = React.useRef<any>();
        const { localize } = useLocalization();
        const ref = React.useRef<HTMLSpanElement>();
        const tokensContainerRef = React.useRef<HTMLSpanElement>();
        const hasConflict = React.useMemo(() => conflicts.some(c => c), [conflicts]);
        const flatTokens = React.useMemo(() => flatten(linesTokens), [linesTokens]);
        const ariaLabel = React.useMemo(() => makeLabelerAriaLabel(localize)(flatTokens, labels), [flatTokens, labels]);

        const { isResizing, onResize } = useResizeBehavior(labels, onLabelResize);

        const disabledTokenSelection = isResizing || disabled;
        const hideHoverBrackets = isResizing || disableHoverBrackets;

        const tokenSelectionStore = React.useMemo(
            () => new TokenSelectionStore({ disabled: disabledTokenSelection, hideHoverBrackets }),
            []
        );

        React.useEffect(() => {
            tokenSelectionStore.setConfig({ disabled: disabledTokenSelection, hideHoverBrackets });
        }, [disabledTokenSelection, hideHoverBrackets]);

        React.useImperativeHandle(labelerRef, () => ({
            cancelSelection: () => tokenSelectionStore.cancel(),
            htmlElement: tokensContainerRef.current
        }));

        useGlobalLabelingEventListeners({
            ref,
            text,
            onRemoveRange,
            tokenSelectionStore,
            tokenizedText: flatTokens,
            isCharacterTokenization: useCharTokenization
        });

        const getLabelingEventListeners = useTokenLabelingEventListeners(tokenSelectionStore);

        const handleSelection = React.useCallback(
            (newSelection?: Selection) => {
                if (!newSelection) {
                    onSelect();

                    return;
                }

                const target =
                    getTokenElementByIndex(ref.current, newSelection[1]) ??
                    getTokenElementByIndex(ref.current, newSelection[0]) ??
                    getFirstTokenElement(ref.current);

                onSelect(newSelection, target);
            },
            [onSelect]
        );

        useSelectionEffect({ onSelect: handleSelection, tokenSelectionStore });

        const handleShapeClick = React.useCallback(
            (label: MultilineLabel, target: Target) => {
                tokenSelectionStore.cancel();
                // LABELING_TODO: This is hack fix for state sync
                setTimeout(() => {
                    if (label.renderedAs === 'box') {
                        onShapeClick(label.originalLabel, target);
                    } else {
                        const tokenTarget = getTokenElementByIndex(ref.current, label.start);

                        onShapeClick(label.originalLabel, tokenTarget);
                    }
                }, 100);
            },
            [onShapeClick, onSelect]
        );

        const tokenEventListeners = React.useCallback(
            (tokenIndex: number) =>
                mergeFunctionObjects(getLabelingEventListeners(tokenIndex), { onMouseEnter: () => onTokenHover(tokenIndex) }),
            [getLabelingEventListeners, onTokenHover]
        );

        const handleShapeRender = React.useCallback(
            (layoutManagerShapeProps: ShapeUIProps, hidden: boolean) => (
                <Shape
                    {...layoutManagerShapeProps}
                    hidden={hidden}
                    onResize={onResize}
                    key={layoutManagerShapeProps.key}
                    containerId={containerId}
                    viewOptions={viewOptions}
                    onClick={handleShapeClick}
                    tooltipContent={labelTooltipContent}
                    disableResize={disableResizingLabels}
                    color={resolveShapeColor(layoutManagerShapeProps)}
                    useCharTokenization={useCharTokenization}
                />
            ),
            [viewOptions, handleShapeClick, onResize, disableResizingLabels, resolveShapeColor, useCharTokenization]
        );

        const handleTokenRender = React.useCallback(
            (layoutManagerTokenProps: TokenUIProps, absoluteTokenIndex: number) => (
                <Token
                    isRTL={isRTL}
                    conflicts={conflicts}
                    key={absoluteTokenIndex}
                    tokenEventListeners={tokenEventListeners}
                    tokenIndex={absoluteTokenIndex}
                    viewOptions={viewOptions}
                    tokenSelectionStore={tokenSelectionStore}
                    useCharTokenization={useCharTokenization}
                    {...layoutManagerTokenProps}
                />
            ),
            [conflicts, viewOptions, tokenEventListeners, isRTL, tokenSelectionStore, useCharTokenization]
        );

        const handleMouseUp = React.useCallback(() => {
            if (tokenSelectionStore.selection[0] !== INVALID_INDEX) {
                tokenSelectionStore.stopDragging();
            }
        }, [tokenSelectionStore]);

        return (
            <FocusZone
                data-id="Labeler"
                data-is-focusable="true"
                onMouseLeave={onTokenHoverExit}
                onMouseUp={handleMouseUp}
                disabled={disabled}
                componentRef={refFocusZone}
                style={{ display: 'flex', flex: 1, height: '100%' }}
                onFocusCapture={_e => {
                    if (refFocusZone.current._root.current === _e.target) {
                        getFirstFocusable(tokensContainerRef.current, tokensContainerRef.current, true)?.focus();
                    }
                }}
                doNotAllowFocusEventToPropagate
                direction={FocusZoneDirection.horizontal}>
                <LayoutManager
                    useVirtualization={useVirtualization}
                    aria-label={ariaLabel}
                    tokensContainerRef={tokensContainerRef}
                    disabled={disabled}
                    isResizing={isResizing}
                    ref={ref}
                    isRTL={isRTL}
                    className={className}
                    style={style}
                    labels={labels}
                    linesTokens={linesTokens}
                    onTokenRender={handleTokenRender}
                    onShapeRender={handleShapeRender}
                    hasConflict={hasConflict}
                    viewOptions={viewOptions}
                    containerId={containerId}
                    tokenSelectionStore={tokenSelectionStore}
                    {...rest}
                />
            </FocusZone>
        );
    })
);

Labeler.displayName = 'Labeler';

/**
 * Factory function to create aria label for the labeler.
 */
const makeLabelerAriaLabel = (localize: ReturnType<typeof useLocalization>['localize']) => (flatTokens: string[], labels: Label[]) => {
    const makeLabelsGroupAriaLabel = (labelsGroup: Label[]) => {
        const text = flatTokens.slice(labelsGroup[0].start, labelsGroup[0].end + 1).join(' ');
        const labelsDisplay = labelsGroup.map(({ entity: { name } }) => name);

        return localize(messages.labelAriaLabel, { text, labelsDisplay: oxfordJoin(labelsDisplay, localize(messages.conjunction)) });
    };

    const sortLabels = (labelsToBeSorted: Label[]) =>
        orderBy(labelsToBeSorted, ['start', (label: Label) => label.end - label.start, 'depth'], ['asc', 'desc', 'asc']);

    const groupedLabels = groupLabelsByBoundaries(sortLabels(labels.filter(label => label.renderedAs === 'line')));
    const groupedPredictions = groupLabelsByBoundaries(sortLabels(labels.filter(label => label.renderedAs === 'box')));

    const originalText = flatTokens.join(' ');
    const labelsAriaLabel = oxfordJoin(
        groupedLabels.map(makeLabelsGroupAriaLabel),
        localize(messages.conjunction),
        localize(messages.emptyLabels)
    );
    const predictionsAriaLabel = oxfordJoin(
        groupedPredictions.map(makeLabelsGroupAriaLabel),
        localize(messages.conjunction),
        localize(messages.emptyPredictions)
    );

    return localize(messages.labelerAriaLabel, { originalText, labelsAriaLabel, predictionsAriaLabel });
};
